import React, { useState, useEffect } from 'react';
import ProductActivity from './ProductActivity';
import PlatformDistribution from './PlatformDistribution';
import SoldProducts from './SoldProducts';
import ImageProcessing from './ImageProcessing';
import LaborSavings from './LaborSavings';
import ProductValue from './ProductValue';
import api from '../../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    totalProducts: 0,
    todayProducts: 0,
    activeMarketplaces: 0,
    timeSaved: 0,
    costSaved: 0
  });

  const animateCount = (key, target) => {
    let current = 0;
    const increment = target / 60;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      setCounts(prev => ({ ...prev, [key]: Math.floor(current) }));
    }, 30);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await api.get('/api/dashboard/summary');
        console.log('Dashboard Summary API Response:', response.data);
        const result = response.data;
        if (result && result.success && result.data) {
          const apiData = {
            totalProducts: result.data.totalProducts || 0,
            todayProducts: result.data.todayProducts || 0,
            activeMarketplaces: result.data.activeMarketplaces || 0,
            timeSaved: result.data.timeSaved || 0,
            costSaved: result.data.costSaved || 0
          };
          // Start counter animations
          setTimeout(() => {
            Object.entries(apiData).forEach(([key, value]) => {
              animateCount(key, value);
            });
          }, 200);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Values remain at 0 on error (initial state)
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // NOTE: Trend values removed - they were hardcoded and not based on real data.
  // To add real trends, backend would need to track historical snapshots.
  const statsData = [
    {
      title: 'Total Products Uploaded',
      value: counts.totalProducts.toLocaleString(),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><rect x="9" y="9" width="6" height="6" /></svg>,
      trend: '',
      period: 'all time'
    },
    {
      title: 'Products Today',
      value: counts.todayProducts.toLocaleString(),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><path d="m12 1-3 6h6l-3-6z" /><path d="m12 23 3-6h-6l3 6z" /><path d="m20 9-6 3 6 3v-6z" /><path d="m4 9 6 3-6 3v-6z" /></svg>,
      trend: '',
      period: 'today'
    },
    {
      title: 'Active Marketplaces',
      value: counts.activeMarketplaces.toLocaleString(),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" /><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" /></svg>,
      trend: '',
      period: 'connected'
    },
    {
      title: 'Labor Hours Saved',
      value: counts.timeSaved.toLocaleString(),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" /></svg>,
      trend: '',
      period: 'estimated'
    },
    {
      title: 'Cost Saved',
      value: `$${counts.costSaved.toLocaleString()}`,
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
      trend: '',
      period: 'estimated'
    }
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-skeleton">
          <div className="skeleton-header"></div>
          <div className="skeleton-cards">
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
          </div>
          <div className="skeleton-charts">
            <div className="skeleton-chart"></div>
            <div className="skeleton-chart"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="professional-dashboard">
      <div className="page-header">
        <h1 className="dashboard-title">Analytics Dashboard</h1>
        <p className="dashboard-subtitle">Monitor your product listing automation and business performance</p>
      </div>

      <div className="dashboard-content">
        {/* TOP SECTION - STATS CARDS & PLATFORM DISTRIBUTION IN SAME GRID */}
        <div className="top-grid-section">
          <div className="kpi-section">
            <div className="kpi-cards-container">
              {statsData.map((stat, index) => (
                <div key={index} className="kpi-col">
                  <div className="kpi-card dashboard-card">
                    <div className="kpi-card-body">
                      <div className="kpi-icon">{stat.icon}</div>
                      <div className="kpi-value">{stat.value}</div>
                      <div className="kpi-title">{stat.title}</div>
                      <div className="kpi-trend">
                        <span className="trend-value">{stat.trend}</span>
                        <span className="trend-period">{stat.period}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="platform-section">
            <PlatformDistribution />
          </div>
        </div>

        {/* PRODUCT UPLOAD ACTIVITY */}
        <div className="dashboard-row mb-4">
          <div className="dashboard-col-12">
            <ProductActivity />
          </div>
        </div>

        {/* SOLD PRODUCTS & PRODUCT VALUE */}
        <div className="dashboard-row mb-4">
          <div className="dashboard-col-4 mb-4-mobile">
            <SoldProducts />
          </div>
          <div className="dashboard-col-8">
            <ProductValue />
          </div>
        </div>

        {/* LABOR SAVINGS & IMAGE PROCESSING */}
        <div className="dashboard-row mb-4">
          <div className="dashboard-col-8 mb-4-mobile">
            <LaborSavings />
          </div>
          <div className="dashboard-col-4">
            <ImageProcessing />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;